/* global Vue, VeeValidate, VueI18n, DATA_LAYER, AZURE_URI, LOCALE */

import { Notifications, setSuccessAndStopLoading, setErrorAndStopLoading, startLoading, stopLoading } from './components/Notifications'
import { messagesSpecific } from '../i18n/translations'
import { CreateI18n } from '../i18n/create-i18n'
import '../validation-rules'

const i18n = CreateI18n(messagesSpecific)

const ContactUsApp = Vue.createApp({
  name: 'ContactUsApp',
  components: { Notifications },
  template: '#contact-us-app-template',

  setup () {
    const { t } = VueI18n.useI18n()
    const form = Vue.reactive({})
    const formName = 'Contact us'
    const formErrors = Vue.reactive({})
    const formElement = Vue.ref(null)
    const childAges = Vue.ref([])
    const validationSchema = {
      name: 'required|min:3',
      email: 'required|email',
      subject: 'required|min:3',
      startDate: 'required',
      endDate: 'required',
      adults: 'required',
      children: 'required',
    }
    const properties = Vue.reactive([])
    const urlParams = new URLSearchParams(window.location.search)
    const propertyId = urlParams.get('property')
    form.childAges = ''

    const { handleSubmit, handleReset, errors, isSubmitting } = VeeValidate.useForm({ validationSchema })

    const fieldRefs = Vue.reactive({})
    Object.keys(validationSchema).forEach((field) => {
      const fieldObj = VeeValidate.useField(field, undefined, {
        validateOnValueUpdate: false,
      })
      form[field] = fieldObj.value
      formErrors[field] = fieldObj.errorMessage
      fieldRefs[field] = fieldObj
    })

    Vue.watch(isSubmitting, () => {
      if (Object.keys(errors).length > 0 && formElement.value) {
        const highlight = Array.from(formElement.value.querySelectorAll('.field-error')).filter(er => er.innerHTML)[0] || null
        if (highlight) { highlight.parentNode.scrollIntoView({ behavior: 'smooth' }) }
      }
    })

    async function getProperties () {
      startLoading('getProperties')
      try {
        Object.assign(properties, null)
        const response = await fetch(`${AZURE_URI}GetProperties?locale=${LOCALE}`)
        if (!response.ok) {
          throw new Error(response.statusText)
        }
        Object.assign(properties, (await response.json()).filter(property => property.phobsId != null))
        properties.sort((a, b) => (a.propertyListingWeight ?? Number.MAX_VALUE) - (b.propertyListingWeight ?? Number.MAX_VALUE))

        if (propertyId != null) {
          form.property = properties.find(property => property.phobsId === propertyId)?.title
        }

        stopLoading('getProperties')
      } catch (err) {
        setErrorAndStopLoading(err)
      }
    }

    Vue.watch(() => form.children, (newVal) => {
      const current = childAges.value.length
      if (newVal > current) {
        for (let i = current; i < newVal; i++) {
          childAges.value.push(null)
        }
      } else {
        childAges.value.splice(newVal)
      }
    })

    Vue.watch(childAges, (newAges) => {
      form.childAges = newAges.filter(age => age !== null && age !== '').join(',')
    }, { deep: true })

    getProperties()

    if (urlParams.get('subject')) {
      form.subject = urlParams.get('subject')
    }

    const sendForm = handleSubmit(async () => {
      DATA_LAYER?.recordUserInteraction('contact_form_submit')
      startLoading(formName)

      try {
        const response = await fetch('/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: `${new URLSearchParams(form).toString()}&form-name=${formName}`,
        })
        if (!response.ok) {
          throw new Error(response.statusText)
        }
        form.message = ''
        form.property = ''
        handleReset()
        setSuccessAndStopLoading({ details: t('formSubmitSuccess') })
      } catch (err) {
        setErrorAndStopLoading(err)
      }
    })

    function validateField (field) {
      fieldRefs[field].validate()
    }

    return {
      properties,
      form,
      formElement,
      formErrors,
      sendForm,
      validateField,
      childAges,
    }
  },
})

const appElement = document.getElementById('contact-us-app')

if (appElement) {
  ContactUsApp.use(i18n)
  ContactUsApp.mount(appElement)
}
